import { useTranslation } from 'react-i18next'
import { useCallback } from 'react'

const LANGUAGE_MAP = Object.freeze({
  uk: 'en-GB',
  en: 'en-GB',
  de: 'de-DE',
  nl: 'nl-NL',
  fr: 'fr-BE',
})

const CURRENCY = 'GBP'

/**
 * Hook to format numbers based on the current i18n language
 * @returns {{
 * format: (number: number, options?: Intl.NumberFormatOptions) => string,
 * formatCurrency: (number: number) => string,
 * locale: string,
 * language: string
 * }}
 */
const useNumberFormatter = () => {
  let {
    i18n: { language },
  } = useTranslation()

  const locale = LANGUAGE_MAP[language] || 'en-GB'

  const format = useCallback(
    /**
     * Format a number based on the current locale
     * @param {number} number The number to format
     * @param {Intl.NumberFormatOptions} [options] The options to pass to the number formatter
     * @returns {string} The formatted number
     */ (number, options) => number.toLocaleString(locale, options),
    [locale],
  )

  const formatCurrency = useCallback(
    /**
     * Format a number as a currency based on the current locale
     * @param {number} number The number to format
     * @returns {string} The formatted currency
     */
    (number) => format(number, { style: 'currency', currency: CURRENCY }),
    [format],
  )

  return {
    format,
    formatCurrency,
    locale,
    language,
  }
}

export default useNumberFormatter
